import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageHelmet from "../component/common/Helmet";
import HeaderAbs from "../component/header/HeaderAbs";
import FooterTwo from "../component/footer/FooterTwo";

function slugifyHeadingId(text) {
    return String(text || "")
        .toLowerCase()
        .trim()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
}

function extractText(node) {
    if (node === null || node === undefined) return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(extractText).join("");
    if (typeof node === "object" && node.props && node.props.children) return extractText(node.props.children);
    return "";
}

function stripLeadingH1(markdown) {
    if (!markdown) return "";
    // Remove a single leading "# Title" line (plus following blank line(s)).
    // Keep the markdown file readable for downloads while avoiding duplicate H1 in the viewer.
    return markdown.replace(/^# .*\n+(?:\s*\n)*/m, "");
}

function parseHeadings(markdown) {
    if (!markdown) return [];
    const lines = markdown.split("\n");
    const headings = [];

    for (const line of lines) {
        const match = /^(#{2,4})\s+(.+?)\s*$/.exec(line);
        if (!match) continue;
        const level = match[1].length;
        const text = match[2].replace(/\s+#*$/, "").trim();
        const id = slugifyHeadingId(text);
        if (!id) continue;
        headings.push({ level, text, id });
    }

    return headings;
}

const RUNBOOKS = {
    legalmind: {
        title: "LegalMind",
        subtitle: "Runbook — Contract Intelligence Accelerator (public-safe)",
        description:
            "Operational runbook and architecture notes for a governed contract intelligence copilot: schema-first extraction, cited Q&A, secure streaming, observability, and rollout controls.",
        canonical: "https://www.theomefford.com/runbooks/legalmind",
        markdownPath: "/assets/runbooks/legalmind-runbook.md",
        diagramUrl: "/assets/images/proof/legalmind-architecture.png",
        backTo: { label: "Back to Enterprise AI", to: "/enterprise-ai#marketplace" },
    },
    careflow: {
        title: "Careflow",
        subtitle: "Security Posture — Ambient Clinical Documentation (public-safe)",
        description:
            "Security posture and operational controls for a regulated, PHI-bearing, real-time voice agent workflow: private connectivity, encryption, access control, auditability, and safe rollout patterns.",
        canonical: "https://www.theomefford.com/runbooks/careflow",
        markdownPath: "/assets/runbooks/careflow-security-posture.md",
        diagramUrl: "/assets/images/proof/careflow-architecture.png",
        backTo: { label: "Back to Enterprise AI", to: "/enterprise-ai#marketplace" },
    },
    healthbridge: {
        title: "HealthBridge",
        subtitle: "Operator Guide — Guardrail-First Agent Navigator (public-safe)",
        description:
            "Operator guide for a dry-run-by-default portal navigation agent runtime: approvals, config gating, evidence-grade status records, monitoring, and incident response.",
        canonical: "https://www.theomefford.com/runbooks/healthbridge",
        markdownPath: "/assets/runbooks/healthbridge-operator-guide.md",
        diagramUrl: "/assets/images/proof/healthbridge-architecture.png",
        backTo: { label: "Back to Enterprise AI", to: "/enterprise-ai#marketplace" },
    },
};

const Runbook = () => {
    const { slug } = useParams();
    const meta = RUNBOOKS[slug];

    const [rawMarkdown, setRawMarkdown] = useState("");
    const [loadError, setLoadError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setIsLoading(true);
            setLoadError(null);
            setRawMarkdown("");

            if (!meta) {
                setIsLoading(false);
                return;
            }

            try {
                const res = await fetch(meta.markdownPath, { cache: "no-store" });
                if (!res.ok) {
                    throw new Error(`Failed to load runbook (${res.status})`);
                }
                const text = await res.text();
                if (!cancelled) {
                    setRawMarkdown(text || "");
                    setIsLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setLoadError(err);
                    setIsLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    const markdownForViewer = useMemo(() => stripLeadingH1(rawMarkdown), [rawMarkdown]);
    const toc = useMemo(() => parseHeadings(markdownForViewer), [markdownForViewer]);

    const structuredData = useMemo(() => {
        if (!meta) return null;
        return {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            headline: `${meta.title} — Runbook`,
            description: meta.description,
            url: meta.canonical,
            author: { "@type": "Person", name: "Theo Mefford" },
        };
    }, [meta]);

    if (!meta) {
        return (
            <React.Fragment>
                <PageHelmet
                    pageTitle="Runbook"
                    description="Runbook not found."
                    canonical="https://www.theomefford.com/runbooks"
                    robots="noindex,follow"
                />
                <HeaderAbs homeLink="/" logo="symbol-dark" color="color-black" />
                <div className="rn-page-title-area pt--120 pb--70 bg_color--1">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="blog-single-page-title text-center pt--60">
                                    <h2 className="title theme-gradient">Runbook not found</h2>
                                    <p className="description">
                                        Unknown runbook slug: <code>{slug}</code>
                                    </p>
                                    <p className="mt--20">
                                        <Link to="/enterprise-ai#marketplace">← Back to Enterprise AI</Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <FooterTwo />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <PageHelmet
                pageTitle={`${meta.title} Runbook`}
                description={meta.description}
                canonical={meta.canonical}
                robots="noindex,follow"
                structuredData={structuredData}
            />

            <HeaderAbs homeLink="/" logo="symbol-dark" color="color-black" />

            <div className="rn-page-title-area pt--120 pb--70 bg_color--1">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="blog-single-page-title text-center pt--60">
                                <h2 className="title theme-gradient">{meta.title}</h2>
                                <p className="description">{meta.subtitle}</p>
                                <div className="mt--30 d-flex justify-content-center flex-wrap" style={{ gap: "12px" }}>
                                    <a
                                        className="rn-button-style--2 btn-solid"
                                        href={meta.diagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Architecture diagram
                                    </a>
                                    <a
                                        className="rn-button-style--2 rn-btn-dark"
                                        href={meta.markdownPath}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Download Markdown
                                    </a>
                                    <Link className="rn-button-style--2 rn-btn-dark" to={meta.backTo.to}>
                                        {meta.backTo.label}
                                    </Link>
                                </div>
                                <p className="description mt--20" style={{ maxWidth: 980, margin: "0 auto" }}>
                                    <strong>Note:</strong> The raw <code>.md</code> files under <code>/assets/runbooks</code>{" "}
                                    are intentionally public-safe. This page renders them with a readable layout (tables, code,
                                    checklists, and anchored navigation).
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="rn-runbook-area pt--80 pb--120 bg_color--5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-3 col-md-12 col-12">
                            <div className="rn-runbook-toc">
                                <h4 className="title">On this page</h4>
                                {toc.length ? (
                                    <ul>
                                        {toc.map((h) => (
                                            <li key={h.id} className={`rn-runbook-toc__item level-${h.level}`}>
                                                <a href={`#${h.id}`}>{h.text}</a>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="description">
                                        {isLoading ? "Loading…" : "No headings found."}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="col-lg-9 col-md-12 col-12">
                            <div className="rn-runbook-markdown">
                                <figure className="rn-runbook-figure">
                                    <a
                                        href={meta.diagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`${meta.title} architecture diagram (open full size)`}
                                    >
                                        <img
                                            className="rn-runbook-figure__image"
                                            src={meta.diagramUrl}
                                            alt={`${meta.title} architecture diagram`}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </a>
                                    <figcaption className="rn-runbook-figure__caption">
                                        Architecture diagram (click to open full size).
                                    </figcaption>
                                </figure>

                                {loadError ? (
                                    <div className="rn-runbook-callout rn-runbook-callout--error">
                                        <h4 className="title">Failed to load runbook</h4>
                                        <p className="description">
                                            Could not fetch <code>{meta.markdownPath}</code>.
                                        </p>
                                        <pre>
                                            <code>{String(loadError && loadError.message ? loadError.message : loadError)}</code>
                                        </pre>
                                    </div>
                                ) : null}

                                {isLoading ? (
                                    <p className="description">Loading runbook…</p>
                                ) : (
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            h2: ({ children }) => {
                                                const text = extractText(children);
                                                const id = slugifyHeadingId(text);
                                                return (
                                                    <h2 id={id} className="rn-runbook-h rn-runbook-h2">
                                                        {children}
                                                    </h2>
                                                );
                                            },
                                            h3: ({ children }) => {
                                                const text = extractText(children);
                                                const id = slugifyHeadingId(text);
                                                return (
                                                    <h3 id={id} className="rn-runbook-h rn-runbook-h3">
                                                        {children}
                                                    </h3>
                                                );
                                            },
                                            h4: ({ children }) => {
                                                const text = extractText(children);
                                                const id = slugifyHeadingId(text);
                                                return (
                                                    <h4 id={id} className="rn-runbook-h rn-runbook-h4">
                                                        {children}
                                                    </h4>
                                                );
                                            },
                                            code: ({ inline, className, children, ...props }) => {
                                                const match = /language-(\w+)/.exec(className || "");
                                                if (inline) {
                                                    return (
                                                        <code className="rn-runbook-code-inline" {...props}>
                                                            {children}
                                                        </code>
                                                    );
                                                }
                                                return (
                                                    <pre className="rn-runbook-code-block">
                                                        <code
                                                            className={match ? `language-${match[1]}` : undefined}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </code>
                                                    </pre>
                                                );
                                            },
                                            a: ({ href, children, ...props }) => {
                                                const isExternal = typeof href === "string" && href.startsWith("http");
                                                return (
                                                    <a
                                                        href={href}
                                                        {...props}
                                                        target={isExternal ? "_blank" : undefined}
                                                        rel={isExternal ? "noopener noreferrer" : undefined}
                                                    >
                                                        {children}
                                                    </a>
                                                );
                                            },
                                        }}
                                    >
                                        {markdownForViewer}
                                    </ReactMarkdown>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FooterTwo />
        </React.Fragment>
    );
};

export default Runbook;

